import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { isRtlLang } from '../i18n';

interface HeaderProps {
  currentLanguage: string;
  toggleLanguage: (language: string) => void;
}
interface NavItem {
  path: string;
  label?: string;
  icon?: string;
  target?: string;
}
const Header: React.FC<HeaderProps> = ({ currentLanguage, toggleLanguage }) => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const isRTL = isRtlLang(currentLanguage);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [currentLanguage]);

  const navItems: NavItem[] = [
    { path: '/', label: t('header.home'), target: '_self' },
    { path: '/about', label: t('header.about'), target: '_self' },
    { path: '/services', label: t('header.services'), target: '_self' },
    { path: '/contact', label: t('header.contact'), target: '_self' },
  ];

  // Close mobile menu when clicking outside or on link
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleClickOutside = () => {
      setIsMobileMenuOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <header
      className="py-2 px-6 md:px-12 sticky top-0 z-50"
      style={{ backgroundColor: '#0a0a0a' }}
      onMouseLeave={() => setIsLanguageDropdownOpen(false)}
    >
      <div
        className={`container mx-auto flex justify-between items-center ${
          isRTL ? 'flex-row-reverse' : ''
        }`}
      >
        <Link
          to="/"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Logo size="sm" />
        </Link>

        {/* Desktop Navigation */}
        <nav className={`hidden md:flex ${isRTL ? 'ml-6' : 'mr-6'}`}>
          <ul className="flex">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  target={item.target || '_self'}
                  rel="noopener noreferrer"
                  className={`text-white hover:text-blue-500 transition-colors duration-300 font-medium ${
                    isRTL ? 'ml-8' : 'mr-8'
                  }`}
                >
                  {item.icon ? (
                    <span>
                      <i
                        className={`${item.icon} text-xl text-green-500 hover:text-green-300`}
                      ></i>
                    </span>
                  ) : (
                    <span className="pb-1">{item.label}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Language Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setIsLanguageDropdownOpen(true)}
          >
            <button
              className={`${
                isLanguageDropdownOpen ? 'text-blue-500' : 'text-white'
              } hover:text-blue-500  duration-300 `}
              onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
            >
              <i
                className="fa fa-globe text-xl"
                aria-hidden="true"
              ></i>
            </button>
            {isLanguageDropdownOpen && (
              <div
                className="absolute right-0 w-32 text-white shadow-lg rounded-md"
                style={{ zIndex: 100, backgroundColor: '#0a0a0a' }}
              >
                <ul className="py-2">
                  <li
                    className={`px-4 py-2 hover:text-blue-500 cursor-pointer mr-2 ${
                      currentLanguage === 'ar' ? 'font-bold text-blue-500' : ''
                    }`}
                    onClick={() => toggleLanguage('ar')}
                  >
                    العربية
                  </li>
                  <li
                    className={`px-4 py-2 hover:text-blue-500 cursor-pointer mr-2 ${
                      currentLanguage === 'en' ? 'font-bold text-blue-500' : ''
                    }`}
                    onClick={() => toggleLanguage('en')}
                  >
                    English
                  </li>
                  <li
                    className={`px-4 py-2 hover:text-blue-500 cursor-pointer mr-2 ${
                      currentLanguage === 'de' ? 'font-bold text-blue-500' : ''
                    }`}
                    onClick={() => toggleLanguage('de')}
                  >
                    Deutsch
                  </li>
                  <li
                    className={`px-4 py-2 hover:text-blue-500 cursor-pointer mr-2 ${
                      currentLanguage === 'fr' ? 'font-bold text-blue-500' : ''
                    }`}
                    onClick={() => toggleLanguage('fr')}
                  >
                    French
                  </li>
                  <li
                    className={`px-4 py-2 hover:text-blue-500 cursor-pointer mr-2 ${
                      currentLanguage === 'es' ? 'font-bold text-blue-500' : ''
                    }`}
                    onClick={() => toggleLanguage('es')}
                  >
                    Spanish
                  </li>
                  <li
                    className={`px-4 py-2 hover:text-blue-500 cursor-pointer mr-2 ${
                      currentLanguage === 'pr' ? 'font-bold text-blue-500' : ''
                    }`}
                    onClick={() => toggleLanguage('pr')}
                  >
                    فارسي
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white ml-4"
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Canvas */}
      {isMobileView && (
        <div
          className={`fixed top-0 z-50 h-full w-full bg-black bg-opacity-70 transition-opacity duration-300 ease-in-out ${
            isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          } ${isRTL ? 'left-0' : ' right-0'}`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            dir={isRTL ? 'rtl' : 'ltr'}
            className={`absolute top-0 h-full w-full  bg-gray-900 shadow-xl transform transition-transform duration-300 ease-in-out} ${
              isMobileMenuOpen
                ? 'translate-y-0'
                : isRTL
                ? '-translate-y-full'
                : 'translate-y-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-start">
              <div
                className={`flex justify-between items-center mb-8 ${
                  isRTL ? 'flex-row-reverse' : ''
                }`}
              >
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Logo
                    size="sm"
                    className={`${isRTL ? 'ml-2' : 'mr-2'}`}
                  />
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white text-2xl"
                >
                  &times;
                </button>
              </div>

              <ul className="space-y-6">
                {navItems.map((item, index) => (
                  <li key={index}>
                    <Link
                      to={item.path}
                      target={item.target || '_self'}
                      rel="noopener noreferrer"
                      className="text-white hover:text-yellow-400 text-xl block py-3"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.icon ? (
                        <div
                          className={`flex items-center ${
                            isRTL ? 'flex-row' : ''
                          }`}
                        >
                          <i
                            className={`${item.icon} text-2xl text-green-500 ${
                              isRTL ? 'ml-3' : 'mr-3'
                            }`}
                          ></i>
                        </div>
                      ) : (
                        item.label
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
